import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Delete, Loader2 } from "lucide-react";
import { useEmployeeSession } from "@/hooks/useEmployeeSession";
import { useMonteurT } from "@/i18n/monteur";
import { primeAudioContext, playNewTasksChime } from "@/lib/notificationChime";
import { toast } from "sonner";
import { LegalFooter } from "@/components/LegalFooter";

/**
 * PIN-Login für Mitarbeiter (Phase 6, Feb 2026)
 *
 * Mobile-first Numeric-Pad. Designziel: funktioniert auf jedem alten
 * Privat-Handy ohne App-Installation und ohne weitere Eingabefelder.
 *
 * Features:
 *  - 4-stelliger PIN
 *  - Großes Tastenfeld (Touch-Targets >= 64 px)
 *  - „Punkte"-Anzeige für jede eingegebene Ziffer
 *  - Auto-Submit sobald 4 Ziffern eingegeben sind
 *  - Bei Fehler: kurze Shake-Animation + sofortiges Leeren
 *  - 429-Rate-Limit-Antwort wird als Toast angezeigt
 *
 * CLARTEXT SPRACHE V1 – Phase 2: UI-Texte über t(). Vor dem Login existiert noch
 * keine Mitarbeiter-Session → t() löst (auch bei späterem Flag AN) auf Deutsch
 * auf, da keine Mitarbeitersprache verfügbar ist.
 */
export default function PinLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, login, loading } = useEmployeeSession();
  const t = useMonteurT();
  const [pin, setPin] = useState("");

  // IMPLEMENT-18 (Juni 2026): weltgebundener QR-Zugang. Trägt das Plakat-QR den
  // Param `?welt=<course_id>`, landet der Mitarbeiter NACH dem PIN-Login direkt in
  // genau dieser Dokumentationswelt (`/monteur/welt/<id>?qr=1`) — ohne Dashboard /
  // Welt-Auswahl. Fehlt der Param, bleibt der bisherige Flow (zur Aufgabenliste).
  const weltId = searchParams.get("welt");
  const postLoginPath = weltId
    ? `/monteur/welt/${weltId}?qr=1`
    : "/monteur";
  const [companyCode, setCompanyCode] = useState(() => {
    try {
      return localStorage.getItem("last_company_code") || "";
    } catch {
      return "";
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    // Wer schon eingeloggt ist, geht direkt zur Aufgabenliste. Wir nutzen
    // useEffect statt direkten Navigate, weil sich `session` asynchron
    // ändert (Session-Hydration nach Reload).
    if (loading) return;
    // AUTH-SICHERHEIT V2 · Phase 2: Session-Marker ist employee.id (Metadaten),
    // nicht mehr Token — der Token liegt im httpOnly-Cookie.
    if (session?.employee?.id) {
      navigate(postLoginPath, { replace: true });
    }
  }, [session, loading, navigate, postLoginPath]);

  const submitPin = (fullPin) => {
    if (submitting) return;
    const code = companyCode.trim();
    if (!code) {
      toast.error(t("login.errorNoCode"));
      setPin("");
      return;
    }
    setSubmitting(true);
    // Neu-seit-letztem-Besuch: AudioContext JETZT (User-Geste des Tastendrucks)
    // anlegen, damit der Chime nach der Server-Antwort die Autoplay-Policy erfüllt.
    primeAudioContext();
    login(code, fullPin)
      .then((result) => {
        // Dezenter Hinweiston NUR bei echten neuen Zuweisungen und NICHT im
        // QR-Deeplink-Flow (dort wird die Aufgabenliste gar nicht angezeigt).
        if (!weltId && result?.newAssignmentsCount > 0) playNewTasksChime();
        navigate(postLoginPath, { replace: true });
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || t("login.errorUnknown");
        toast.error(detail);
        setShake(true);
        setTimeout(() => setShake(false), 400);
        setPin("");
        setSubmitting(false);
      });
  };

  const appendDigit = (d) => {
    if (submitting) return;
    setPin((p) => {
      if (p.length >= 4) return p;
      const next = p + d;
      // Auto-Submit sobald 4 Ziffern eingegeben sind.
      if (next.length === 4) {
        submitPin(next);
      }
      return next;
    });
  };
  const backspace = () => {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
  };

  return (
    <div
      className="min-h-screen bg-slate-900 flex flex-col"
      data-testid="pin-login-page"
    >
      <header className="px-5 py-4 flex items-center">
        <button
          onClick={() => navigate("/")}
          className="text-slate-400 hover:text-white p-2 -ml-2"
          data-testid="pin-login-back"
          aria-label={t("login.backAria")}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="text-center mb-10">
          <img
            src="/brand/clartext-logo-full-dark.svg"
            alt="CLartext"
            className="h-16 sm:h-[72px] w-auto max-w-full object-contain mx-auto mb-5"
            data-testid="app-logo-employee-login"
          />
          <div className="text-slate-400 text-sm uppercase tracking-widest mb-2">
            {t("login.eyebrow")}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">
            {t("login.title")}
          </h1>
          <p className="text-slate-400 mt-3 text-sm">
            {t("login.subtitle")}
          </p>
        </div>

        {/* Firmen-Code (Sprint G.2: Mandanten-Zuordnung) */}
        <div className="w-full max-w-xs mb-8">
          <label
            htmlFor="emp-company-code"
            className="block text-xs font-medium text-slate-400 mb-1.5 text-center uppercase tracking-widest"
          >
            {t("login.companyCodeLabel")}
          </label>
          <input
            id="emp-company-code"
            data-testid="employee-company-code-input"
            value={companyCode}
            onChange={(e) =>
              setCompanyCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
            }
            placeholder={t("login.companyCodePlaceholder")}
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={16}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-700 bg-slate-800 text-white text-center text-lg tracking-[0.2em] font-semibold placeholder:text-slate-600 focus:border-white focus:outline-none transition-colors"
          />
        </div>

        {/* PIN-Punkte */}
        <div
          className={`flex items-center gap-4 mb-12 ${shake ? "animate-shake" : ""}`}
          data-testid="pin-display"
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-5 h-5 rounded-full border-2 transition-all ${
                pin.length > i
                  ? "bg-white border-white"
                  : "border-slate-600 bg-transparent"
              }`}
              data-testid={`pin-dot-${i}`}
            />
          ))}
        </div>

        {/* Tastenfeld */}
        <div
          className="grid grid-cols-3 gap-3 w-full max-w-xs"
          data-testid="pin-keypad"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <KeyButton
              key={n}
              onClick={() => appendDigit(String(n))}
              disabled={submitting}
              testId={`pin-key-${n}`}
            >
              {n}
            </KeyButton>
          ))}
          <div /> {/* leerer Slot für 9er-Layout */}
          <KeyButton
            onClick={() => appendDigit("0")}
            disabled={submitting}
            testId="pin-key-0"
          >
            0
          </KeyButton>
          <KeyButton
            onClick={backspace}
            disabled={submitting || pin.length === 0}
            testId="pin-key-backspace"
            variant="dim"
          >
            <Delete className="w-6 h-6" />
          </KeyButton>
        </div>

        {submitting && (
          <div
            className="mt-8 flex items-center gap-2 text-slate-400 text-sm"
            data-testid="pin-submitting"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("login.submitting")}
          </div>
        )}
      </main>

      {/* ABO-RECHT-02, Betreiberfreigabe 27.10.2026 — Rechtslinks klein unter dem PIN-Feld. */}
      <LegalFooter className="px-6 pb-6" />

      {/* Inline-Animationen */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          50% { transform: translateX(8px); }
          75% { transform: translateX(-4px); }
        }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}

function KeyButton({ children, onClick, disabled, testId, variant }) {
  const base =
    "h-16 sm:h-20 rounded-full font-heading text-2xl sm:text-3xl font-light transition-all active:scale-95 select-none flex items-center justify-center";
  const colors =
    variant === "dim"
      ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
      : "bg-white/10 text-white hover:bg-white/15";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${base} ${colors} disabled:opacity-40`}
    >
      {children}
    </button>
  );
}
